/**
 * AlurackRoofEditExtra — UI for Alurack's extra engineering fields rendered
 * inside the library's `roofEditExtra` sidebar slot.
 *
 * Mirrors the field-level behavior of `RenderInputsPerSystem`
 * (common/components/segment) but without react-hook-form since the library
 * owns the settings state. All decisions about which fields to show, what
 * options are valid, and how strategy+value is persisted come from the same
 * `systemsValues` helpers the main Alurack forms use — so editing after
 * creation produces the same payload shape as creation.
 *
 * The library passes us:
 *   - `selectedSettings`: the current extra settings for the selected roof
 *   - `updateExtraSetting(key)`: returns a setter `(value) => void` that
 *     forwards the change back into the library store and triggers an
 *     onSegmentUpdate round-trip.
 *
 * NO outer Accordion here — the library wraps the whole slot in a single
 * "Estructura" accordion. This component renders its fields flat.
 */
import React, { useMemo } from 'react';

import {
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@sunwisesoftware/sunwise-ui';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { Tooltip as InfoPopover } from 'common/components';
import {
    getFieldsToRender,
    getSlopeOptions,
} from 'common/utils/helpers/systemsValues';

import * as createProposalSelectors from '../../createProposalModal/selectors';
function AlurackRoofEditExtra({
    isRectangle,
    selectedSettings,
    updateExtraSetting,
}) {
    const { t } = useTranslation();

    const typeApplication = selectedSettings?.type_application || '';
    const systemUid = selectedSettings?.system_uid || '';
    const sloofType = selectedSettings?.sloof_type || '';

    // Note: `system_name` backfill for legacy segments is handled at the
    // parent level in AlurackSolarPlanner so ALL roofs (selected or not)
    // get hydrated at load time. This component only renders the editable
    // engineering fields; it no longer needs to fetch system metadata.

    const fieldsToRender = useMemo(
        () => getFieldsToRender(systemUid),
        [systemUid],
    );
    const slopeOptions = useMemo(() => getSlopeOptions(systemUid), [systemUid]);
    const has = (key) => fieldsToRender.includes(key);

    // sloof_type is only meaningful for rectangles
    const showSloof = has('sloof_type');

    return (
        <Stack spacing={2}>
            {typeApplication ? (
                <>
                    {showSloof && slopeOptions.length > 0 && (
                        <>
                            {/* Render the select and an info popover to its right */}
                            <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                            >
                                <TextField
                                    fullWidth
                                    label={t('Slope type')}
                                    onChange={(e) =>
                                        updateExtraSetting('sloof_type')(
                                            e.target.value,
                                        )
                                    }
                                    select
                                    size="small"
                                    disabled={!isRectangle}
                                    value={sloofType}
                                >
                                    {slopeOptions.map((opt) => (
                                        <MenuItem
                                            key={opt.value}
                                            value={opt.value}
                                        >
                                            {opt.label}
                                        </MenuItem>
                                    ))}
                                </TextField>

                                {!isRectangle && (
                                    <InfoPopover sx={{ mt: 0 }}>
                                        <Typography variant="body2">
                                            {t(
                                                'Slope type info non-rectangular',
                                            )}
                                        </Typography>
                                    </InfoPopover>
                                )}
                            </Stack>
                        </>
                    )}
                </>
            ) : (
                <Typography color="text.secondary" variant="caption">
                    {t(
                        'Set a system type in the creation form to edit engineering fields.',
                    )}
                </Typography>
            )}
        </Stack>
    );
}

AlurackRoofEditExtra.propTypes = {
    isRectangle: PropTypes.bool,
    selectedSettings: PropTypes.object,
    updateExtraSetting: PropTypes.func.isRequired,
};

const mapStateToProps = createStructuredSelector({
    roofTypes: createProposalSelectors.getRoofTypesData,
    systemTypes: createProposalSelectors.getSystemTypesData,
});

export default connect(mapStateToProps)(AlurackRoofEditExtra);
